// src/features/loyalty/api/useLoyaltyLevel.ts
import { useApiQuery, useApiMutation, ApiResponse } from "../../../api/useApi";
import { useQueryClient } from "react-query";
import type { LoyaltyLevelBenefit, LoyaltyLevelBenefitPublic } from "./useLoyaltyLevelBenefit";

// --- TYPES ---
export interface TLoyaltyLevel {
    locale: string;
    name: string;
    description: string;
}

export interface LoyaltyLevel {
    id: number;
    loyalty_program_id: number;
    image_id: number | null;
    quantity: number;
    color: string | null;
    order?: number;
    translates: TLoyaltyLevel[];
    benefits?: LoyaltyLevelBenefit[];
}

// --- HOOKS ---
const getQueryKey = (programId: number | string) => `/loyalty-programs/${programId}/loyalty-levels`;

export function useLoyaltyLevels(programId: number | string | undefined) {
    return useApiQuery<ApiResponse<LoyaltyLevel[]>>(
        programId ? getQueryKey(programId) : '',
        {},
        { enabled: !!programId }
    );
}

export function useCreateLoyaltyLevel(programId: number | string) {
    const queryClient = useQueryClient();
    return useApiMutation<LoyaltyLevel, unknown, Omit<LoyaltyLevel, 'id' | 'loyalty_program_id'>>(
        "post",
        () => `/loyalty-programs/${programId}/loyalty-levels`,
        {
            onSuccess: () => {
                queryClient.invalidateQueries(getQueryKey(programId));
            }
        }
    );
}

export function useUpdateLoyaltyLevel(programId: number | string) {
    const queryClient = useQueryClient();
    return useApiMutation<LoyaltyLevel, unknown, Partial<LoyaltyLevel> & { id: number }>(
        "put",
        (variables) => `/loyalty-levels/${variables.id}`,
        {
            onSuccess: () => {
                queryClient.invalidateQueries(getQueryKey(programId));
            }
        }
    );
}

export function useDeleteLoyaltyLevel(programId: number | string) {
    const queryClient = useQueryClient();
    return useApiMutation<void, unknown, { id: number }>(
        "delete",
        (variables) => `/loyalty-levels/${variables.id}`,
        {
            onSuccess: () => {
                queryClient.invalidateQueries(getQueryKey(programId));
            }
        }
    );
}

export type LoyaltyLevelPublic = {
    id: number;
    quantity: number;
    color?: string | null;
    name?: string | null;
    description?: string | null;
    image_url?: string | null;
    benefits?: LoyaltyLevelBenefitPublic[];
};

export function useLoyaltyLevelsPublic(programId?: number | string) {
    const url = programId ? `/loyalty-programs/${programId}/loyalty-levels-public` : "";
    return useApiQuery<ApiResponse<LoyaltyLevelPublic[]>>(url, {}, { enabled: !!programId });
}